import { I18N_DOMAIN } from '../../../editor/settings';

const { __ } = wp.i18n;

/**
 * Posts to the poll-votes custom endpoint.
 *
 * @param {string} votedOptionId - The poll option unique id.
 * @param {string} questionId - The poll unique id
 */
const addVotes = async (votedOptionId, questionId, postType) =>
	wp
		.apiFetch({
			path: `/mavis/v1/poll-survey-votes`,
			method: 'POST',
			data: {
				question_id: questionId,
				option_id: votedOptionId,
				post_type: postType,
			},
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Stores votes to window.localStorage.
 *
 * @param {string} questionId - The poll unique id
 * @param {string} votedOptionId - The poll option unique id.
 */
const storeVotes = (questionId, votedOptionId, postType) => {
	const existingData = JSON.parse(
		window.localStorage.getItem(`MAVIS_${postType.toUpperCase()}_VOTE`) || '[]',
	);

	const registerVote = {
		questionID: questionId,
		answerID: votedOptionId,
	};

	// push new data to array with exisiting
	existingData.push(registerVote);

	// Set the selected poll ID, poll option ID to localStorage
	// Save back to localStorage
	window.localStorage.setItem(`MAVIS_${postType.toUpperCase()}_VOTE`, JSON.stringify(existingData));
};

/**
 * Updates the percentages based on vote.
 *
 * @param {string} votedOptionId - The poll option unique id.
 * @param {string} questionId - The poll unique id
 */
const updateVotes = async (votedOptionId, questionId, showResults = true, postType) => {
	// Adds votes to post_meta.
	const votes = await addVotes(votedOptionId, questionId, postType);

	const uppercase = postType === 'poll' ? 'Poll' : 'Survey';

	// Add `is-selected` class to the chosen option.
	const selectedPollContainer = document.querySelector(`
		.mavis${uppercase}Results[data-${postType}-id="${questionId}"] .mavis${uppercase}Result[data-option-id="${votedOptionId}"]
	`);
	selectedPollContainer.classList.add('is-selected');

	// Update vote percentages.
	votes.forEach((option) => {
		const resultNum = document.querySelector(`
			.mavis${uppercase}Results[data-${postType}-id="${questionId}"] .mavis${uppercase}Result[data-option-id="${option.id}"] .mavis${uppercase}Result-percentage
		`);
		const resultBar = document.querySelector(`
			.mavis${uppercase}Results[data-${postType}-id="${questionId}"] .mavis${uppercase}Result[data-option-id="${option.id}"] progress
		`);
		resultBar.value = option.vote_percentage;
		resultNum.innerHTML = `${option.vote_percentage}%`;
	});

	const results = document.querySelector(
		`.mavis${uppercase}Results[data-${postType}-id="${questionId}"]`,
	);

	// Get `orderBy` data
	const { orderBy } = results.dataset;

	// Reorder the results by `vote_percentage` if needed.
	if (orderBy === 'votes') {
		const frag = document.createDocumentFragment();
		const options = results.querySelectorAll(`
			.mavis${uppercase}Results[data-${postType}-id="${questionId}"] .mavis${uppercase}Result
		`);
		const sortedResults = Array.from(options).sort((a, b) => {
			const c = a.querySelector('progress').value;
			const d = b.querySelector('progress').value;

			if (c > d) {
				return -1;
			}
			if (c < d) {
				return 1;
			}
			return 0;
		});

		sortedResults.forEach((option) => {
			frag.appendChild(option);
		});

		results.appendChild(frag);
	}

	// Reveal results if showResults is true.
	if (showResults) {
		// Remove the form.
		const questionToRemove = document.querySelector(`
			.wp-block-mavis-${postType}[data-${postType}-id="${questionId}"] fieldset .wp-block-group
			`);
		questionToRemove.remove();

		// Remove submit button when results are shown
		const removeSubmit = document.querySelector(`
			.wp-block-mavis-${postType}[data-${postType}-id="${questionId}"] .wp-block-button
		`);

		if (removeSubmit) {
			removeSubmit.remove();
		}

		// Remove help text from Polls e.g. 'You can only choose one'
		const textToRemove = [
			...document.querySelectorAll(
				`.wp-block-mavis-${postType}[data-${postType}-id="${questionId}"] .mavisPoll-assuranceText, .wp-block-mavis-${postType}[data-${postType}-id="${questionId}"] .mavisPoll_multiSelectHelp`,
			),
		];
		if (textToRemove.length > 0) {
			textToRemove.forEach((el) => el.remove());
		}

		results.classList.add('has-voted');

		// Focus the privacy notice link to stop focus jumping to the body.
		document
			.querySelector(
				`
			.wp-block-mavis-${postType}[data-${postType}-id="${questionId}"]
		`,
			)
			?.parentNode?.querySelector('.privacy-notice-link')
			?.focus();
	}

	// Store votes in the window.
	storeVotes(questionId, votedOptionId, postType);
};

/**
 * Displays the existing votes if there is any.
 * @param {string} postType 'poll' or 'survey'
 */
const displayExistingVotes = (postType) => {
	// get localStorage
	const userVotes = JSON.parse(window.localStorage.getItem(`MAVIS_${postType.toUpperCase()}_VOTE`));
	// if userVotes is NOT null loop over
	if (userVotes) {
		const uppercase = postType === 'poll' ? 'Poll' : 'Survey';

		userVotes.forEach((vote) => {
			// get poll/survey and option ID of the vote.
			const voteQuestionId = vote.questionID;
			const voteOptionId = vote.answerID;

			// Check the poll/survey is on the page.
			// TODO: Make this more effecient by checking the polls on the page are in MAVIS_VOTE.
			const questionToRemove =
				document.querySelector(`
			.wp-block-mavis-${postType}[data-${postType}-id="${voteQuestionId}"] form
			`) ||
				document.querySelector(`
			.wp-block-mavis-${postType}[data-${postType}-id="${voteQuestionId}"] fieldset
			`);
			if (questionToRemove) {
				// Remove the form/fieldset depending on which is present.
				questionToRemove.remove();

				// get results of voted poll/survey
				const votedQuestionResults = document.querySelector(`
				.mavis${uppercase}Results[data-${postType}-id="${voteQuestionId}"]
				`);

				// get previously voted option
				const votedQuestionOption = document.querySelector(`
				.mavis${uppercase}Result[data-option-id="${voteOptionId}"]
				`);

				// add has-voted class to results to show them without 'click'
				votedQuestionResults.classList.add('has-voted');

				// add is-selected class to previously voted poll option without 'click'
				votedQuestionOption.classList.add('is-selected');
			}
		});
	}
};

/**
 * Add event listeners to each poll/survey on the save.
 *
 * @param {string} postType 'poll' or 'survey' .
 * @param {array} questionCollection All poll or survey elements.
 */
const addOptionClickedListeners = (postType, questionCollection) => {
	questionCollection.forEach((question) => {
		const { multipleChoice } = question.dataset;

		// eslint-disable-next-line complexity
		question.addEventListener('click', (event) => {
			// Get the closest poll the event target
			const isMultipleChoice = event.target.closest(`.wp-block-mavis-${postType}`);

			if (multipleChoice) {
				if (isMultipleChoice.dataset.multipleChoice === multipleChoice) {
					if (!event.target.parentElement.classList.contains('is-selected')) {
						event.target.parentElement.classList.add('is-selected');
					} else {
						event.target.parentElement.classList.remove('is-selected');
					}
				}
			}

			const triggers = multipleChoice ? ['BUTTON'] : ['INPUT'];

			// Ignore if clicked outside of input/label.
			if (!triggers.includes(event.target.tagName)) {
				return;
			}

			const questionId = postType === 'poll' ? question.dataset.pollId : question.dataset.surveyId;

			const optionParent = event.target.parentElement;

			if (optionParent.classList.contains(`wp-block-mavis-${postType}-option`)) {
				optionParent.classList.add('is-selected');
			}

			const pollSurveyName = event.target
				.closest(`.wp-block-mavis-${postType}`)
				.getAttribute(`data-${postType}-name`);

			if (multipleChoice) {
				event.preventDefault();

				/**
				 * If the question is multiple choice, get all inputs that are checked and store their ID's in an array.
				 * Pass each ID to the store/udpate functions.
				 */
				const rawSelectedOptions = [...question.getElementsByTagName('input')].filter(
					(input) => input.checked,
				);
				const selectedOptions = rawSelectedOptions.map((input) => input.id);
				const selectedOptionsText = rawSelectedOptions.map((input) => input.getAttribute('title'));

				if (window.dataLayer) {
					// Push votes to GA.
					window.dataLayer.push({
						event: `${postType}Complete`,
						multipleChoice: !!multipleChoice,
						selectedOptions: selectedOptionsText,
						[`${postType}Name`]: pollSurveyName,
					});
				}

				/**
				 * Ensures our votes are added in series and not parallel like it would if we used a forEach.
				 * More details: https://plainenglish.io/blog/async-await-foreach#2-the-arrayreduce-solution
				 */
				selectedOptions.reduce(async (referencePoint, optionId, index) => {
					await referencePoint;
					const showResults = index === selectedOptions.length - 1 || false;
					await updateVotes(optionId, questionId, showResults, postType);
				}, Promise.resolve());
			} else {
				const votedOptionId = event.target.value;
				const optionText = event.target.getAttribute('title');

				if (window.dataLayer) {
					// Push votes to GA.
					window.dataLayer.push({
						event: `${postType}Complete`,
						optionText,
						[`${postType}Name`]: pollSurveyName,
						multipleChoice: !!multipleChoice,
					});
				}

				// Update the vote post_meta, vote percetages, reorder votes and remove options.
				updateVotes(votedOptionId, questionId, true, postType);
			}
		});
	});
};

/**
 * Set postType and postId to localStorage when a user decides to skip
 *
 * @param {string} postType 'poll' or 'survey' .
 * @param {array} allElements All poll or survey elements.
 */
const listenForSkips = (postType, allElements) => {
	const uppercase = postType === 'poll' ? 'Poll' : 'Survey';

	// Loop over surveys and polls to find its skip button
	allElements.forEach((element) => {
		const skipButton = element.querySelector('.mavisSkip-button');

		// Listen for a click on that button and remove the element instantly
		skipButton.addEventListener('click', (button) => {
			if (button.target.classList.contains('mavisSkip-button')) {
				const name = button.target
					.closest(`.wp-block-mavis-${postType}`)
					.getAttribute(`data-${postType}-name`);

				if (window.dataLayer) {
					window.dataLayer.push({
						event: `${postType}Interaction`,
						interactionType: 'skip',
						name,
					});
				}

				// Find the closest post type container and remove it
				element.closest(`.mavis${uppercase}-container`).remove();

				// Grab the current date on click
				const whenSkipped = new Date().getTime();

				// Return correct ID based on postType
				const elementID =
					postType === 'poll'
						? button.target.parentElement.dataset.pollId
						: button.target.parentElement.dataset.surveyId;

				// Set the postType and ID to an object ready for localStorage
				const toBeSkipped = {
					postType,
					postId: elementID,
					whenSkipped,
				};

				// Check for already stored skipped polls and surveys
				const checkForSkipped = JSON.parse(window.localStorage.getItem('MAVIS_SKIPPED_ELEMENTS'));

				// If no polls or surveys have been skipped yet store their post type and id to localStorage
				if (checkForSkipped === null) {
					window.localStorage.setItem('MAVIS_SKIPPED_ELEMENTS', JSON.stringify([toBeSkipped]));
				} else {
					// If some polls or surveys HAVE been skipped, grab them
					const getCurrentStorage = JSON.parse(
						window.localStorage.getItem('MAVIS_SKIPPED_ELEMENTS'),
					);
					// Push the new values to an array containing the old values
					getCurrentStorage.push(toBeSkipped);

					// Set everything back to localStorage
					window.localStorage.setItem('MAVIS_SKIPPED_ELEMENTS', JSON.stringify(getCurrentStorage));
				}
			}
		});
	});
};

/**
 * Check that an answer has been selected, if not, show an error message
 *
 * @param {string} postType 'poll' or 'survey' .
 * @param {array} allElements All poll or survey elements.
 */
const answerValidation = (postType, allElements) => {
	const uppercase = postType === 'poll' ? 'Poll' : 'Survey';
	// Loop through all elements
	allElements.forEach((element) => {
		// Add event listener for each one
		element.addEventListener('click', (event) => {
			// If click is on an a tag
			if (event.target.tagName === 'BUTTON') {
				// Get all the inputs for that element, and filter the checked ones
				const selectedOptions = [...element.getElementsByTagName('input')].filter(
					(input) => input.checked,
				);

				// If no inputs are checked
				if (selectedOptions.length === 0) {
					// Get the span that will contain the error message
					const validationElement = element.querySelector(`.mavis${uppercase}-validation-warning`);
					// Append the error message to the span
					validationElement.innerHTML = __("You haven't selected an option", I18N_DOMAIN);
					// Add is-unanswered class to display the error message
					validationElement.classList.add('is-unanswered');
				} else {
					// Get the span that contains the error message
					const validationElement = element.querySelector(`.mavis${uppercase}-validation-warning`);
					// Remove the error message if an answer has been submitted
					validationElement.innerHTML = '';
					// If one or more inputs are checked, check if the error message is displayed
					if (validationElement.classList.contains('is-unanswered')) {
						// Remove the class to hide the error message
						validationElement.remove();
					}
				}
			}
		});
	});
};

window.addEventListener('DOMContentLoaded', () => {
	// On load check localStorage to see if any polls have been ANSWERED previously
	const isAnsweredPoll = JSON.parse(window.localStorage.getItem('MAVIS_POLL_VOTE'));

	// On load check localStorage to see if any surveys have been ANSWERED previously
	const isAnsweredSurvey = JSON.parse(window.localStorage.getItem('MAVIS_SURVEY_VOTE'));

	if (isAnsweredPoll) {
		// Then loop over each element in the updated storage and remove it
		isAnsweredPoll.forEach((element) => {
			const answeredContent = document.querySelector(
				`.wp-block-mavis-poll[data-poll-id="${element.questionID}"]`,
			);

			if (answeredContent) {
				answeredContent.closest(`.mavisPoll-container`).remove();
			}
		});
	}

	if (isAnsweredSurvey) {
		// Then loop over each element in the updated storage and remove it
		isAnsweredSurvey.forEach((element) => {
			const answeredContent = document.querySelector(
				`.wp-block-mavis-survey[data-survey-id="${element.questionID}"]`,
			);

			if (answeredContent) {
				answeredContent.closest(`.mavisSurvey-container`).remove();
			}
		});
	}

	// On load check localStorage to see if any polls or surveys have been SKIPPED previously
	const isSkipped = JSON.parse(window.localStorage.getItem('MAVIS_SKIPPED_ELEMENTS'));

	// If localStorage has data for skipped elements
	if (isSkipped) {
		// Get the current time
		const currentTime = new Date().getTime();

		// Calculate 30 days in milliseconds
		const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

		// Loop over those elements and use the postType & postId data to get that poll or survey
		isSkipped.forEach((element, key) => {
			// Get the times for both the previously set date and the new date
			const timeDiffInMs = currentTime - element.whenSkipped;

			// If the time difference is more than 30 days
			if (timeDiffInMs >= thirtyDaysInMs) {
				// Remove the stored polls and surveys from the skipped localStorage data
				isSkipped.splice(key, 1);
				// Update the localStorage
				window.localStorage.setItem('MAVIS_SKIPPED_ELEMENTS', JSON.stringify(isSkipped));
			}
		});

		// After localStorage has been updated, grab the updated storage
		const skipper = JSON.parse(window.localStorage.getItem('MAVIS_SKIPPED_ELEMENTS'));

		// Then loop over each element in the updated storage and hide it
		skipper.forEach((element) => {
			const uppercase = element.postType === 'poll' ? 'Poll' : 'Survey';

			const skippedContent = document.querySelector(
				`.wp-block-mavis-${element.postType}[data-${element.postType}-id="${element.postId}"]`,
			);

			if (skippedContent) {
				// Add a class to hide that element
				skippedContent.closest(`.mavis${uppercase}-container`).remove();
			}
		});
	}

	// Get any existing data
	const allPolls = [...document.querySelectorAll('.wp-block-mavis-poll')];
	const allSurveys = [...document.querySelectorAll('.wp-block-mavis-survey')];

	if (allPolls.length > 0) {
		listenForSkips('poll', allPolls);
		displayExistingVotes('poll');
		addOptionClickedListeners('poll', allPolls);
		answerValidation('poll', allPolls);
	}

	if (allSurveys.length > 0) {
		listenForSkips('survey', allSurveys);
		displayExistingVotes('survey');
		addOptionClickedListeners('survey', allSurveys);
		answerValidation('survey', allSurveys);
	}
});
